import { useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  Container,
  Title,
  Paper,
  Stack,
  Text,
  Center,
  Loader,
  Button,
  Group,
  Divider,
  useMantineColorScheme,
  ActionIcon,
  Menu,
  NativeSelect,
  Accordion,
} from '@mantine/core';
import { IconRefresh, IconMenu2 } from '@tabler/icons-react';
import { useTimeAgo } from '@commons/timeAgo';
import { getNotificationColor } from '@commons/notificationColor';
import { useAppDispatch, useAppSelector } from '@store';
import {
  fetchNotifications,
  updateNotificationReadStatus,
} from '@store/slices/notificationsSlice';
import { notifications as mantineNotifications } from '@mantine/notifications';
import {
  FilterSelectionModal,
  findMatchingFilters,
} from '@/users/components/NotificationCenter/FilterSelectionModal';
import { fetchFolderCategoryForNotification } from '@/users/components/NotificationCenter/fetchFolderCategory';
import { buildQueryFoldersSearchFromNotification } from '@/users/components/UserFolders/utils/queryFoldersState';
import routes from '@/commons/routes';
import { useNavigate, useParams } from 'react-router';
import { Notification as NotificationType } from '@/users/components/NotificationCenter/types';
import { NotificationsState } from '@/store/slices/notificationsSlice';

export const UserNotifications = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { username } = useParams();
  const {
    notifications,
    isLoading,
    pagination,
    hasFetched,
  }: NotificationsState = useAppSelector((state) => state.notifications);
  const filters = useAppSelector((state) => state.filters.filters);
  const [filterModalOpened, setFilterModalOpened] = useState(false);
  const [filterModalCategoryHint, setFilterModalCategoryHint] = useState<
    string | undefined
  >(undefined);
  const [selectedNotification, setSelectedNotification] =
    useState<NotificationType | null>(null);
  const [filterType, setFilterType] = useState<string>('');

  // Get unique notification types
  const uniqueTypes = useMemo(() => {
    const types = notifications.map((n) => n.type).filter(Boolean);
    return Array.from(new Set(types)).sort();
  }, [notifications]);

  // Filter notifications by type and normalize read property
  const filteredNotifications = useMemo(() => {
    if (!filterType) {
      return notifications;
    }
    return notifications.filter((n: NotificationType) => n.type === filterType);
  }, [notifications, filterType]);

  // Group consecutive notifications with the same folderName
  const groupedNotifications = useMemo(() => {
    const groups: Array<NotificationType | NotificationType[]> = [];
    let currentGroup: NotificationType[] = [];
    let currentFolderName: string | null = null;

    const closeCurrentGroup = () => {
      if (currentGroup.length > 0) {
        if (currentGroup.length === 1) {
          groups.push(currentGroup[0]);
        } else {
          groups.push([...currentGroup]);
        }
        currentGroup = [];
        currentFolderName = null;
      }
    };

    filteredNotifications.forEach((notification) => {
      const folderName = notification.data?.folderName;

      // If no folderName, add as individual notification
      if (!folderName) {
        closeCurrentGroup();
        groups.push(notification);
        return;
      }

      // If folderName matches current group, add to group
      if (currentFolderName === folderName) {
        currentGroup.push(notification);
        return;
      }

      // Close previous group and start new one
      closeCurrentGroup();
      currentGroup = [notification];
      currentFolderName = folderName;
    });

    // Add final group if exists
    closeCurrentGroup();

    return groups;
  }, [filteredNotifications]);

  useEffect(() => {
    // Only fetch if not already fetched and no notifications are loaded
    // The thunk's condition will prevent duplicate fetches
    if (!hasFetched && notifications.length === 0 && !isLoading) {
      dispatch(fetchNotifications({ skip: 0, limit: 50 }));
    }
  }, [dispatch, hasFetched, notifications.length, isLoading]);

  const loadMore = () => {
    dispatch(
      fetchNotifications({
        skip: pagination.skip + pagination.limit,
        limit: pagination.limit,
      })
    );
  };

  const refresh = () => {
    dispatch(fetchNotifications({ skip: 0, limit: 50, force: true }));
  };

  const toggleReadStatus = async (notificationId: string, read: boolean) => {
    try {
      await dispatch(
        updateNotificationReadStatus({ notificationId, read })
      ).unwrap();
      mantineNotifications.show({
        color: 'green',
        message: intl.formatMessage({
          id: read ? 'notificationMarkedAsRead' : 'notificationMarkedAsUnread',
        }),
        autoClose: 2000,
      });
    } catch (error) {
      console.error('Error updating notification:', error);
      mantineNotifications.show({
        color: 'red',
        title: intl.formatMessage({ id: 'error' }),
        message: intl.formatMessage({ id: 'errorUpdatingNotification' }),
      });
    }
  };

  const handleOpenFilter = (filterId: string, folderName: string) => {
    const filterDef = filters.find((f: any) => f.id === filterId);
    if (!username) {
      return;
    }
    const base = routes.routes.queryFolders.view(username, filterId);
    const qs = buildQueryFoldersSearchFromNotification(filterDef, folderName);
    navigate(qs ? `${base}?${qs}` : base);
  };

  const handleOpenFilterModal = async (notification: NotificationType) => {
    const folderName = notification.data?.folderName;
    if (!folderName) {
      return;
    }

    try {
      const category = await fetchFolderCategoryForNotification(folderName);
      const availableFilters = findMatchingFilters(filters, folderName, {
        category,
      });

      if (availableFilters.length === 1) {
        handleOpenFilter(availableFilters[0].id, folderName);
        return;
      }

      setFilterModalCategoryHint(category);
      setSelectedNotification(notification);
      setFilterModalOpened(true);
    } catch {
      setFilterModalCategoryHint(undefined);
      setSelectedNotification(notification);
      setFilterModalOpened(true);
    }
  };

  if (isLoading && notifications.length === 0) {
    return (
      <Container>
        <Center h="50vh">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  return (
    <Container my="md" size="xl" pb={200}>
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <Title order={4}>
              {intl.formatMessage({ id: 'notifications' })}
            </Title>
          </Group>
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={refresh}
              loading={isLoading}
            >
              {intl.formatMessage({ id: 'refresh' })}
            </Button>
            <NativeSelect
              data={[
                { value: '', label: intl.formatMessage({ id: 'all' }) },
                ...uniqueTypes.map((type) => ({
                  value: type,
                  label: intl.formatMessage({ id: `notification.${type}` }),
                })),
              ]}
              value={filterType}
              onChange={(e) => setFilterType(e.currentTarget.value)}
              w={150}
              size="sm"
            />
          </Group>
        </Group>

        {filteredNotifications.length === 0 ? (
          <Paper p="xl" withBorder>
            <Center>
              <Text c="dimmed">
                {intl.formatMessage({ id: 'noNotifications' })}
              </Text>
            </Center>
          </Paper>
        ) : (
          <Stack gap="xs">
            {groupedNotifications.map((group, index) => {
              // If it's a single notification
              if (!Array.isArray(group)) {
                return (
                  <NotificationItem
                    key={group.id}
                    notification={group}
                    onToggleReadStatus={toggleReadStatus}
                    onOpenFilterModal={handleOpenFilterModal}
                  />
                );
              }

              // If it's a group of notifications
              return (
                <NotificationGroup
                  key={`group-${index}-${group[0].id}`}
                  groupIndex={index}
                  notifications={group}
                  onToggleReadStatus={toggleReadStatus}
                  onOpenFilterModal={handleOpenFilterModal}
                />
              );
            })}

            {pagination.hasMore && (
              <Center mt="md">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  loading={isLoading}
                >
                  {intl.formatMessage({ id: 'loadMore' })}
                </Button>
              </Center>
            )}

            <Divider my="sm" />

            <Text size="sm" c="dimmed" ta="center">
              {intl.formatMessage(
                { id: 'showingXofY' },
                {
                  shown: filteredNotifications.length,
                  total: filterType
                    ? filteredNotifications.length
                    : pagination.total,
                }
              )}
            </Text>
          </Stack>
        )}
      </Stack>

      {selectedNotification && (
        <FilterSelectionModal
          opened={filterModalOpened}
          onClose={() => {
            setFilterModalOpened(false);
            setSelectedNotification(null);
            setFilterModalCategoryHint(undefined);
          }}
          folderName={selectedNotification.data?.folderName || ''}
          categoryHint={filterModalCategoryHint}
        />
      )}
    </Container>
  );
};

interface NotificationItemProps {
  notification: NotificationType;
  onToggleReadStatus: (id: string, read: boolean) => void;
  onOpenFilterModal: (notification: NotificationType) => void;
}

interface NotificationGroupProps {
  groupIndex: number;
  notifications: NotificationType[];
  onToggleReadStatus: (id: string, read: boolean) => void;
  onOpenFilterModal: (notification: NotificationType) => void;
}

const NotificationGroup = ({
  groupIndex,
  notifications,
  onToggleReadStatus,
  onOpenFilterModal,
}: NotificationGroupProps) => {
  const intl = useIntl();
  const { colorScheme } = useMantineColorScheme();
  const timeAgoFormatter = useTimeAgo();
  const folderName = notifications[0]?.data?.folderName;
  const hasUnread = notifications.some((n) => !n.read);
  const unreadCount = notifications.filter((n) => !n.read).length;
  const readCount = notifications.filter((n) => n.read).length;

  // Get unique notification types in the group
  const uniqueTypes = Array.from(
    new Set(notifications.map((n) => n.type))
  ).sort();

  // Get the most recent notification time
  const mostRecentTime = notifications.reduce((latest, current) => {
    return new Date(current.createdAt) > new Date(latest.createdAt)
      ? current
      : latest;
  }, notifications[0]);
  const timeAgoText = timeAgoFormatter.format(
    new Date(mostRecentTime.createdAt)
  );

  return (
    <Paper
      withBorder
      style={{
        opacity: !hasUnread ? 0.6 : 1,
        backgroundColor: !hasUnread
          ? 'transparent'
          : colorScheme === 'dark'
            ? 'var(--mantine-color-dark-6)'
            : 'var(--mantine-color-gray-0)',
      }}
    >
      <Accordion
        styles={{
          item: {
            border: 'none',
          },
          content: {
            padding: 0,
          },
        }}
      >
        <Accordion.Item
          value={`group-${groupIndex}-${folderName || 'default'}`}
        >
          <Accordion.Control
            styles={{
              control: {
                padding: 'var(--mantine-spacing-md)',
              },
              label: {
                padding: 0,
              },
              chevron: {
                marginLeft: 'var(--mantine-spacing-sm)',
              },
            }}
          >
            <Group
              gap="sm"
              wrap="nowrap"
              align="flex-start"
              style={{ flex: 1 }}
            >
              <div
                style={{
                  width: 4,
                  backgroundColor: `var(--mantine-color-blue-6)`,
                  borderRadius: 2,
                  flexShrink: 0,
                  alignSelf: 'stretch',
                }}
              />
              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Group gap="sm" justify="space-between" wrap="nowrap">
                  <Text
                    size="sm"
                    fw={500}
                    lineClamp={1}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    {folderName ? (
                      <>
                        {intl.formatMessage({ id: 'folderName' })}: {folderName}
                      </>
                    ) : (
                      intl.formatMessage({ id: 'notifications' })
                    )}
                  </Text>
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    {notifications.length}{' '}
                    {intl.formatMessage({ id: 'notifications' })}
                  </Text>
                </Group>
                <Group gap="xs" wrap="nowrap">
                  {uniqueTypes.length > 0 && (
                    <>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {uniqueTypes
                          .map((type) =>
                            intl.formatMessage({ id: `notification.${type}` })
                          )
                          .join(', ')}
                      </Text>
                      {(unreadCount > 0 || readCount > 0 || timeAgoText) && (
                        <Text size="xs" c="dimmed">
                          •
                        </Text>
                      )}
                    </>
                  )}
                  {unreadCount > 0 && (
                    <Text size="xs" c="blue" fw={500}>
                      {unreadCount}{' '}
                      {intl.formatMessage({
                        id: unreadCount === 1 ? 'unread' : 'unreads',
                      })}
                    </Text>
                  )}
                  {readCount > 0 && unreadCount > 0 && (
                    <Text size="xs" c="dimmed">
                      •
                    </Text>
                  )}
                  {readCount > 0 && (
                    <Text size="xs" c="dimmed">
                      {readCount}{' '}
                      {intl.formatMessage({
                        id: readCount === 1 ? 'read' : 'reads',
                      })}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">
                    • {timeAgoText}
                  </Text>
                </Group>
              </Stack>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs" px="md" pb="md">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onToggleReadStatus={onToggleReadStatus}
                  onOpenFilterModal={onOpenFilterModal}
                />
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Paper>
  );
};

const NotificationItem = ({
  notification,
  onToggleReadStatus,
  onOpenFilterModal,
}: NotificationItemProps) => {
  const intl = useIntl();
  const timeAgoFormatter = useTimeAgo();
  const timeAgoText = timeAgoFormatter.format(new Date(notification.createdAt));
  const { colorScheme } = useMantineColorScheme();

  // Process data to translate nested i18n keys
  const processedData = { ...notification.data };

  return (
    <Paper
      p="md"
      withBorder
      style={{
        opacity: notification.read ? 0.6 : 1,
        backgroundColor: notification.read
          ? 'transparent'
          : colorScheme === 'dark'
            ? 'var(--mantine-color-dark-6)'
            : 'var(--mantine-color-gray-0)',
      }}
    >
      <Group gap="sm" wrap="nowrap" align="flex-start">
        <div
          style={{
            width: 4,
            backgroundColor: `var(--mantine-color-${getNotificationColor(notification.type)}-6)`,
            borderRadius: 2,
            flexShrink: 0,
            alignSelf: 'stretch',
          }}
        />
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Text
            size="sm"
            fw={500}
            lineClamp={1}
            style={{ flex: 1, minWidth: 0 }}
          >
            {intl.formatMessage({
              id: `notification.${notification.type}`,
            })}
          </Text>
          <Text
            size="xs"
            c="dimmed"
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {intl.formatMessage(
              { id: `notification.${notification.i18nKey}` },
              processedData || {}
            )}
            .
          </Text>
        </Stack>
        <Stack gap={4} align="flex-end" style={{ flexShrink: 0 }}>
          <Text size="xs" c="dimmed">
            {timeAgoText}
          </Text>
          <Menu position="bottom-end" withinPortal withArrow>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="sm">
                <IconMenu2 size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {notification.data?.folderName && (
                <Menu.Item onClick={() => onOpenFilterModal(notification)}>
                  {intl.formatMessage({ id: 'openInFilter' })}
                </Menu.Item>
              )}
              <Menu.Item
                onClick={() =>
                  onToggleReadStatus(notification.id, !notification.read)
                }
              >
                {intl.formatMessage({
                  id: notification.read ? 'markAsUnread' : 'markAsRead',
                })}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Stack>
      </Group>
    </Paper>
  );
};
